import { createPortal } from 'react-dom'
import { ErrorBoundary } from 'react-error-boundary'
import { isEmpty, pipe } from 'remeda'
import type { EmptyObject } from 'type-fest'
import { CarouselsStack } from '@components/CarouselsStack/CarouselsStack'
import { CartDrawer } from '@components/CartDrawer/CartDrawer'
import { CategoriesBlock } from '@components/CategoriesBlock/CategoriesBlock'
import Header from '@components/Header'
import renderLiquidLinks from '@components/Header/utils/renderLiquidLinks'
import { Hero } from '@components/Hero/Hero'
import { InstagramFeed } from '@components/InstagramFeed/InstagramFeed'
import type { InstagramFeedProps } from '@components/InstagramFeed/InstagramFeed.types'
import {
  withApollo,
  withErrorBoundary,
  withExchangeRates,
  withI18next,
  withSkeletonTheme,
  withStaffPickImage,
  withTheme,
} from '@hocs'
import type { CartDrawerData } from '@velasca/types'
import { AboutUsBlock } from '@velasca/ui/about-us-block'
import { BottegasBlock } from '@velasca/ui/bottegas-block'
import { CollectionFocusBlock } from '@velasca/ui/collection-focus-block'
import { ErrorFallback } from '@velasca/ui/error-fallback'
import { HeroPlaceholder } from '@velasca/ui/hero-placeholder'
import { RedirectBlock } from '@velasca/ui/redirect-block'

interface HomepageAppParams {
  heroData: any
  collectionFocusSlidesData: any
  carouselsStackData: any
  aboutUsData: any
  bottegasBlockData: any
  redirectBlockData: any
  cartDrawerData: CartDrawerData
  headerEl: HTMLElement | null
  headerData: any
  promotionalBannerData: any
  categoriesBlocksData: any
  instagramFeedData: InstagramFeedProps | EmptyObject
}

const HomePageApp = ({
  heroData,
  collectionFocusSlidesData,
  carouselsStackData,
  aboutUsData,
  bottegasBlockData,
  redirectBlockData,
  cartDrawerData,
  headerEl,
  headerData,
  promotionalBannerData,
  categoriesBlocksData,
  instagramFeedData,
}: HomepageAppParams) => {
  return (
    <>
      <CartDrawer emptyCartCtas={cartDrawerData?.ctas || []} />
      {headerEl &&
        createPortal(
          <Header
            promotionalBanner={promotionalBannerData || {}}
            miscHeaderData={headerData?.miscData}
            menu={
              renderLiquidLinks(
                headerData?.nav,
                headerData?.miscData?.liquidLinks,
              ) ?? undefined
            }
            lowerMenu={
              headerData?.mobileOnlyNav &&
              renderLiquidLinks(
                headerData?.mobileOnlyNav,
                headerData?.miscData?.liquidLinks,
              )
            }
          />,
          headerEl,
        )}
      <ErrorBoundary FallbackComponent={HeroPlaceholder}>
        <Hero {...heroData} />
      </ErrorBoundary>
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <CategoriesBlock {...categoriesBlocksData} />
      </ErrorBoundary>
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <CollectionFocusBlock slides={collectionFocusSlidesData} />
      </ErrorBoundary>
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <CarouselsStack {...carouselsStackData} />
      </ErrorBoundary>
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <AboutUsBlock {...aboutUsData} />
      </ErrorBoundary>
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <BottegasBlock {...bottegasBlockData} />
      </ErrorBoundary>
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <RedirectBlock {...redirectBlockData} />
      </ErrorBoundary>
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        {!isEmpty(instagramFeedData) && (
          /* @ts-expect-error */
          <InstagramFeed {...instagramFeedData} />
        )}
      </ErrorBoundary>
    </>
  )
}

export const HomePageAppWrapper = pipe(
  HomePageApp,
  withStaffPickImage,
  withExchangeRates,
  withSkeletonTheme,
  withI18next,
  withApollo,
  withErrorBoundary,
  withTheme,
)
